import type { ApiPaginatedResponse, ApiPaginationQuery } from '@_/types';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

export const paginationCursorValidator = z
  .object({
    after: z.string().trim().nonempty().optional().catch(undefined),
    before: z.string().trim().nonempty().optional().catch(undefined)
  })
  .refine(value => !(value.before && value.after) && !!(value.before || value.after));

type PaginationCursor = z.infer<typeof paginationCursorValidator>;

const cursorValidator = paginationCursorValidator.optional().catch(undefined);

interface BaseOptions<TData> {
  enabled?: boolean;
  keepPreviousData?: boolean;
  reactQueryOptions: (paginationQuery: ApiPaginationQuery) => {
    queryKey: QueryKey;
    queryFn: () => Promise<ApiPaginatedResponse<TData[]>>;
  };
}

type Options<TData> =
  | (BaseOptions<TData> & {
      controlled: true;
      cursor: PaginationCursor | undefined;
      onChangeCursor: (cursor: PaginationCursor | undefined) => void;
    })
  | (BaseOptions<TData> & {
      controlled?: false;
      initialCursor?: PaginationCursor | undefined;
    });

interface ReturnType<TData> {
  cursor: PaginationCursor | undefined;
  hasNext: boolean;
  hasPrevious: boolean;
  items: TData[];
  nextCursor: string | null | undefined;
  nextPage: () => void;
  previousCursor: string | null | undefined;
  previousPage: () => void;
  query: UseQueryResult<ApiPaginatedResponse<TData[]>>;
  totalItems: number | undefined;
}

export function usePaginatedList<TData>(options: Options<TData>): ReturnType<TData> {
  const { controlled, enabled, reactQueryOptions } = options;

  const externalCursor = options.controlled ? options.cursor : undefined;
  const onChangeCursor = options.controlled ? options.onChangeCursor : undefined;

  const [internalCursor, setInternalCursor] = useState<PaginationCursor | undefined>(() =>
    !options.controlled ? options.initialCursor : undefined
  );

  const cursor = useMemo<PaginationCursor | undefined>(() => {
    const result = cursorValidator.safeParse(controlled ? externalCursor : internalCursor);
    return result.success ? result.data : undefined;
  }, [controlled, externalCursor, internalCursor]);

  const setCursor = useCallback(
    (newCursor: PaginationCursor | undefined) => {
      if (controlled) {
        onChangeCursor?.(newCursor);
      } else {
        setInternalCursor(newCursor);
      }
    },
    [controlled, onChangeCursor]
  );

  const query = useQuery({
    ...reactQueryOptions({
      afterCursor: cursor?.after,
      beforeCursor: cursor?.before
    }),
    enabled,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined
  });

  const response = query.data;

  const items = useMemo<TData[]>(() => response?.data || [], [response]);

  const prevItemsRef = useRef<TData[]>(undefined);
  if (response) {
    prevItemsRef.current = items;
  }

  const { nextCursor, previousCursor, totalItems } = useMemo(
    () =>
      response?.pagination || {
        nextCursor: undefined,
        previousCursor: undefined,
        totalItems: undefined
      },
    [response]
  );

  const nextPage = useCallback(() => {
    if (nextCursor) {
      setCursor({ after: nextCursor });
    }
  }, [nextCursor, setCursor]);

  const previousPage = useCallback(() => {
    if (previousCursor) {
      setCursor({ before: previousCursor });
    }
  }, [previousCursor, setCursor]);

  return {
    cursor,
    hasPrevious: !!previousCursor,
    hasNext: !!nextCursor,
    items,
    nextCursor,
    nextPage,
    previousCursor,
    previousPage,
    query,
    totalItems
  };
}
